import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { FormInstance } from "../models/FormInstance";
import { getForms } from "../repositories/FormsRepository";
import type { FormsFilters } from "./useFormsFilters";

export const FORM_INSTANCES_QUERY_KEYS = {
	all: ["formInstances"] as const,
	list: (filters: FormsFilters) =>
		[
			"formInstances",
			"list",
			filters.assetId,
			// The raw URL values, not the parsed dates: Date objects are rebuilt on every render and
			// would never compare equal.
			filters.startParam,
			filters.endParam,
			filters.searchText,
			filters.statusCode,
			filters.includeExpired,
		] as const,
};

const EMPTY: FormInstance[] = [];

/**
 * The form instances for the area in the route.
 *
 * `enabled` keeps the request from going out before the area is known. Before this the page fired
 * five times per load and the plant-wide one happened to land last, which is why refreshing showed
 * the wrong asset's list (#1721).
 */
export const useFormInstances = (filters: FormsFilters) => {
	const query = useQuery<FormInstance[], Error>({
		queryKey: FORM_INSTANCES_QUERY_KEYS.list(filters),
		queryFn: ({ signal }) =>
			getForms(
				filters.start,
				filters.end,
				filters.assetId,
				null,
				filters.searchText,
				filters.statusCode,
				filters.includeExpired,
				signal,
			),
		enabled: filters.assetId !== null,
		keepPreviousData: true,
	});

	return {
		rows: query.data ?? EMPTY,
		isLoading: query.isFetching,
		error: query.error,
	};
};

/** Refreshes the form instance list after a write, from anywhere on the page. */
export const useRefreshFormInstances = () => {
	const queryClient = useQueryClient();

	return useCallback(
		() =>
			queryClient.invalidateQueries({
				queryKey: FORM_INSTANCES_QUERY_KEYS.all,
			}),
		[queryClient],
	);
};
